import { useState, useMemo } from "react";
import * as XLSX from "xlsx"; 
import { CompactNumber } from "../../../../../../utils/UniversalFunction";
import { tableStyles, COLUMN_LABELS } from '../helper'

const PAGE_SIZE = 10;

export const TabularView = ({ data, locations = [], title }) => {
  const rows = data;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filters, setFilters] = useState({});
  const [globalSearch, setGlobalSearch] = useState("");

  const columnLabelMap = useMemo(() => {
    return COLUMN_LABELS.reduce((acc, c) => {
      acc[c.rawLabel] = c.replaceLabel;
      return acc;
    }, {});
  }, []);

  const locationMap = useMemo(() => {
    return locations.reduce((acc, loc) => {
      acc[String(loc.value)] = loc.label;
      return acc;
    }, {});
  }, [locations]);

  const { singleValueColumns, tableColumns } = useMemo(() => {
    if (!rows?.length) return { singleValueColumns: {}, tableColumns: [] };
    const single = {};
    const variable = [];
    const allKeys = Object.keys(rows[0]);

    allKeys.forEach(col => {
      if (col === "unit" || col === 'value' || col === 'period_ts' || col === 'module' || col === 'sum_value') return;
      const uniqueValues = new Set(rows.map(r => r[col]).filter(v => v !== null && v !== undefined));
      if (uniqueValues.size === 1) {
        single[col] = [...uniqueValues][0];
      } else {
        variable.push(col);
      }
    });
    return { singleValueColumns: single, tableColumns: variable };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchesColumnFilters = Object.entries(filters).every(([col, searchValue]) => {
        if (!searchValue || searchValue.trim() === "") return true;
        const lowerSearch = searchValue.toLowerCase().trim();
        let valueToCompare = row[col];
        if (col === "location_id") {
          valueToCompare = locationMap[String(row[col])] ?? row[col];
        }
        return String(valueToCompare ?? "").toLowerCase().includes(lowerSearch);
      });

      const matchesGlobalSearch = !globalSearch.trim() || Object.keys(row).some((col) => {
        if (['unit', 'period_ts', 'module', 'value', 'sum_value'].includes(col)) return false;
        let valueToCompare = row[col];
        if (col === "location_id") {
            valueToCompare = locationMap[String(row[col])] ?? row[col];
        }
        return String(valueToCompare ?? "").toLowerCase().includes(globalSearch.toLowerCase().trim());
      });

      return matchesColumnFilters && matchesGlobalSearch;
    });
  }, [rows, filters, globalSearch, locationMap]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredRows, sortConfig]);

  const totalFilteredRows = sortedRows.length;
  const totalPages = Math.ceil(totalFilteredRows / PAGE_SIZE);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows?.slice(start, start + PAGE_SIZE);
  }, [sortedRows, currentPage]);

  const startRange = totalFilteredRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endRange = Math.min(currentPage * PAGE_SIZE, totalFilteredRows);

  const handleDownloadExcel = () => {
    const excelData = sortedRows.map(row => {
      const formattedRow = {};
      Object.entries(singleValueColumns).forEach(([key, val]) => {
        const label = columnLabelMap[key] ?? key;
        formattedRow[label] = key === "location_id" ? locationMap[String(val)] ?? val : val;
      });
      tableColumns.forEach(col => {
        const label = columnLabelMap[col] ?? col;
        formattedRow[label] = col === "location_id" ? locationMap[String(row[col])] ?? row[col] : row[col];
      });
      formattedRow[columnLabelMap["sum_value"] ?? "Value"] = row.sum_value;
      formattedRow["Unit"] = row.unit;
      return formattedRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const fileName = title ? title.replace(/[^a-z0-9]/gi, '_') : "Data_Export";
    XLSX.writeFile(workbook, `${fileName}_${new Date().toLocaleDateString()}.xlsx`);
  };

  const updateFilter = (col, value) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, [col]: value }));
  };

  const removeFilter = (col) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[col];
      return newFilters;
    });
  };

  const handleSort = (col) => {
    setCurrentPage(1);
    setSortConfig(prev => ({
      key: col,
      direction: prev.key === col && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getVisiblePages = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div style={tableStyles.container}>
      {/* 1. HEADER SECTION - REVERSED WRAP FOR SMALL SCREENS */}
      <div style={{ 
        display: "flex", 
        flexDirection: "column-reverse", // Controls go TOP, Badges go BOTTOM on wrap
        padding: "12px 16px", 
        borderBottom: "1px solid #e2e8f0",
        background: "#ffffff",
        gap: "12px"
      }}>
        
        {/* Context Badges Container */}
        <div style={{ 
          display: "flex", 
          flexWrap: "wrap", 
          gap: "8px"
        }}>
          {Object.entries(singleValueColumns).map(([key, value]) => (
            <div key={key} style={{ ...tableStyles.badge, margin: 0, padding: "4px 10px" }}>
              <span style={{ color: "#3f88a5", fontWeight: "700" }}>{columnLabelMap[key] ?? key}:</span>
              <span>{key === "location_id" ? locationMap[String(value)] ?? value : value ?? "-"}</span>
            </div>
          ))}
        </div>

        {/* CONTROLS AREA - Always aligned Left/Right */}
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between", 
          width: "100%", 
          padding: "8px 12px",
          background: "#f8fafc", 
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          gap: "8px"
        }}>
          {/* GLOBAL SEARCH - Left Side */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", flex: "1", maxWidth: "220px" }}>
            <input
              type="text"
              placeholder="Search..."
              value={globalSearch}
              onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setCurrentPage(1);
              }}
              style={{
                  padding: "6px 28px 6px 10px",
                  fontSize: "12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  width: "100%",
                  outline: "none",
                  background: "#ffffff"
              }}
            />
            <div style={{ position: "absolute", right: "8px", display: "flex", alignItems: "center", gap: "2px" }}>
               {globalSearch && (
                  <button 
                    onClick={() => setGlobalSearch("")}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0 }}
                  >
                     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
               )}
               <span style={{ color: "#94a3b8" }}>
                 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
               </span>
            </div>
          </div>
          
          {/* EXCEL BUTTON - Right Side */}
          <button 
            onClick={handleDownloadExcel}
            title="Download Excel"
            style={{
              display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px",
              backgroundColor: "#166534", color: "#ffffff", border: "none",
              borderRadius: "6px", fontSize: "12px", fontWeight: "600", cursor: "pointer",
              transition: "all 0.15s ease-in-out", whiteSpace: "nowrap", height: "32px",
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#14532d"; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "#166534"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Excel
          </button>
        </div>
      </div>

      {/* ... Rest of the component (Filter bar, Table, Footer) remains exactly the same ... */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
          {Object.keys(filters).map(col => (
            <div key={col} style={{ display: "flex", alignItems: "center", background: "white", border: "1px solid #3f88a5", borderRadius: "6px", padding: "4px 10px", gap: "8px" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#3f88a5" }}>{columnLabelMap[col] ?? col}:</span>
              <input autoFocus type="text" value={filters[col]} onChange={(e) => updateFilter(col, e.target.value)} placeholder="Search..." style={{ border: "none", outline: "none", fontSize: "12px", width: "100px" }} />
              <button onClick={() => removeFilter(col)} style={{ border: "none", background: "none", cursor: "pointer", color: "#ef4444", fontWeight: "bold" }}>✕</button>
            </div>
          ))}
          <select value="" onChange={(e) => e.target.value && updateFilter(e.target.value, "")} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px dashed #cbd5e1", fontSize: "12px", color: "#64748b", cursor: "pointer", background: "white", minHeight: "32px", outline: "none" }}>
            <option value="">+ Add Filter</option>
            {tableColumns.map(col => !Object.keys(filters).includes(col) && ( <option key={col} value={col}>{columnLabelMap[col] ?? col}</option> ))}
          </select>
          {(Object.keys(filters).length > 0 || globalSearch) && (
            <button onClick={() => { setFilters({}); setGlobalSearch(""); }} style={{ background: "none", border: "none", color: "#94a3b8", fontSize: "12px", cursor: "pointer", textDecoration: "underline", fontWeight: "600" }}>Reset all</button>
          )}
        </div>
      </div>

      <div style={tableStyles.scrollArea}>
        {paginatedRows.length > 0 ? (
          <table style={tableStyles.table}>
            <thead>
              <tr>
                {tableColumns.map(col => (
                  <th key={col} onClick={() => handleSort(col)} style={{ ...tableStyles.th, textAlign: "left", padding: "12px 16px", backgroundColor: filters[col] ? "#f0f9ff" : "inherit" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      {columnLabelMap[col] ?? col}
                      <span style={{ fontSize: "11px", opacity: sortConfig.key === col ? 1 : 0.4 }}> {sortConfig.key === col ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"} </span>
                    </div>
                  </th>
                ))}
                <th onClick={() => handleSort("sum_value")} style={{ ...tableStyles.th, textAlign: "right", padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}> Value <span style={{ fontSize: "11px", opacity: sortConfig.key === "sum_value" ? 1 : 0.4 }}> {sortConfig.key === "sum_value" ? (sortConfig.direction === "asc" ? " ▲" : " ▼") : " ↕"} </span> </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f9fbfd" }}>
                  {tableColumns.map(col => (
                    <td key={col} style={{...tableStyles.td, padding: "10px 16px" }}> {col === "location_id" ? locationMap[String(row[col])] ?? row[col] : row[col] ?? "-"} </td>
                  ))}
                  <td style={{ ...tableStyles.td, textAlign: "right", padding: "10px 16px" }}>
                    <div style={{ fontWeight: "700" }}> <CompactNumber value={row.sum_value} decimals={2} /> <span style={{ marginLeft: "6px", fontSize: "11px", color: "#94a3b8" }}>{row.unit}</span> </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "#64748b" }}>No matching records found.</div>
        )}
      </div>

      <div style={{ ...tableStyles.footer, display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e8f0", background: "#ffffff", gap: "12px" }}>
        <div style={{ color: "#64748b", fontSize: "13px", flex: "1 1 auto" }}> Showing <strong>{startRange}</strong> to <strong>{endRange}</strong> of <strong>{totalFilteredRows}</strong> records </div>
        {totalPages > 1 && (
          <div style={{ ...tableStyles.paginationGroup, flex: "0 1 auto" }}>
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={{ ...tableStyles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}>Prev</button>
            {getVisiblePages().map(page => ( <button key={page} onClick={() => setCurrentPage(page)} style={{ ...tableStyles.pageBtn, ...(currentPage === page ? tableStyles.activePage : {}) }}>{page}</button> ))}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} style={{ ...tableStyles.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};